import { reportDocumentTableFailure } from "./documentTableFailure";
import { resolveTableDocument } from "./documentTableLookup";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTablePreviewState } from "./useTablePreviewState";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  "store" | "onDownloadOverride" | "documents" | "onDeleteOverride" | "onDocumentDeleted"
> &
  Pick<ReturnType<typeof useTableDialogState>, "versionDocsCache"> &
  Pick<ReturnType<typeof useTablePreviewState>, "setDeleteConfirmState" | "deleteConfirmState">;
export function createTableDocumentActions(options: Options) {
  const {
    store,
    onDownloadOverride,
    documents,
    versionDocsCache,
    setDeleteConfirmState,
    deleteConfirmState,
    onDeleteOverride,
    onDocumentDeleted,
  } = options;
  // Handle single document selection (only when store is available)
  const handleSelectDocument = (docId: string, isSelected: boolean) => {
    if (store) {
      store.selectDocument(docId, isSelected);
    }
  };

  // Handle document download
  const handleDownloadDocument = (docId: string) => {
    if (onDownloadOverride) {
      // Use custom download handler (e.g., for public share view)
      onDownloadOverride(docId);
    } else if (store) {
      // Default: download via store
      store.downloadDocument(docId).catch((error) => {
        reportDocumentTableFailure("document_download", error);
      });
    }
  };

  // Handle document deletion - shows confirmation dialog
  const handleDeleteDocument = (docId: string) => {
    const doc = resolveTableDocument(options, docId);
    if (!doc) return;

    // Show confirmation dialog
    setDeleteConfirmState({
      open: true,
      docId,
      filename: doc.filename,
    });
  };

  // Execute delete after confirmation
  const executeDelete = () => {
    const { docId } = deleteConfirmState;
    if (!docId) return;

    if (onDeleteOverride) {
      // Use custom delete handler (e.g., for restore in trash view)
      onDeleteOverride(docId).catch((error) => {
        reportDocumentTableFailure("document_custom_delete", error);
      });
    } else if (store) {
      // Default: soft delete via store. Notify parent on success so derived
      // state (e.g. /projects folder counts) can refresh.
      store
        .deleteDocument(docId)
        .then(() => onDocumentDeleted?.(docId))
        .catch((error) => {
          reportDocumentTableFailure("document_delete", error);
        });
    }

    // Close dialog
    setDeleteConfirmState({ open: false, docId: null, filename: null });
  };

  // Handle parent document download
  const handleDownloadParent = (parentId: string) => {
    if (onDownloadOverride) {
      onDownloadOverride(parentId);
    } else if (store) {
      store.downloadDocument(parentId).catch((error) => {
        reportDocumentTableFailure("parent_download", error);
      });
    }
  };
  return {
    handleDownloadDocument,
    handleDeleteDocument,
    handleSelectDocument,
    handleDownloadParent,
    executeDelete,
  };
}
